import { AddressComponentService } from '../services/address.component.service';
import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { FormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { NodeAPIService } from './../services/node.api.service';
import * as _ from 'lodash-es';
import { MatchedAddress, FullAddress } from '../models/address.models';
import { DipStateService } from '../services/dip-state-service.service';

/**
 *
 * @export
 * @class AddressLookupComponent
 * @implements {OnInit}
 */
@Component({
    selector: 'app-address-lookup',
    templateUrl: './address.lookup.component.html',
    styleUrls: ['./address.lookup.component.scss'],
    providers: [NodeAPIService],
    standalone: false
})
export class AddressLookupComponent implements OnInit {
  /**
   * Address search form
   *
   * @type {FormGroup}
   * @memberof AddressLookupComponent
   */
  formSearch: UntypedFormGroup;
  /**
   * Search results from address lookup
   *
   * @type {MatchedAddress[]}
   * @memberof AddressLookupComponent
   */
  searchResults: MatchedAddress[];

  /**
  * The iterator of the address. This is required
  * because the iterator MUST be re-inserted back where it was.
  *
  * @type {number}
  * @memberof AddressLookupComponent
  */
  @Input() index: number = 0;


  /**
  * The iterator of the address. This is required
  * because the iterator MUST be re-inserted back where it was.
  *
  * @type {status}
  * @memberof AddressLookupComponent
  */
  status: boolean = false;

  /**
   *
   *
   * @type {EventEmitter<FormGroup>}
   * @memberof AddressLookupComponent
   */
  // tslint:disable-next-line:variable-name
  @Output() lookup_form_out: EventEmitter<UntypedFormGroup> = new EventEmitter<UntypedFormGroup>();

  /**
   * Did the search return any results
   *
   * @readonly
   * @type {boolean}
   * @memberof AddressLookupComponent
   */
  get hasSearchResults(): boolean {
    return this.searchResults.length > 0;
  }

  /**
   * Is the search button enabled
   *
   * @type {boolean}
   * @memberof AddressLookupComponent
   */
  searchButtonEnabled: boolean = true;

  /**
   * Did the address lookup return no results
   *
   * @type {boolean}
   * @memberof AddressLookupComponent
   */
  isSearchReturnedNoResults: boolean = false;

  /**
   *
   *
   * @type {boolean}
   * @memberof AddressLookupComponent
   */
  _isLoading: boolean = false;

  /**
   *
   *
   * @type {FormControl}
   * @memberof AddressLookupComponent
   */
  @Input()
  hasAddressSelected: UntypedFormControl = new UntypedFormControl('', [
    Validators.required,
  ]);

  /**
   *
   * @type {FormControl}
   * @memberof AddressLookupComponent
   */
  @Input()
  hasSearched: UntypedFormControl = new UntypedFormControl(null, [
    Validators.required,
  ]);

  /**
   *
   * @type {FormControl}
   * @memberof AddressLookupComponent
   */
  @Input()
  postcode: UntypedFormControl = new UntypedFormControl(null, [
    Validators.required,
    // tslint:disable-next-line:max-line-length
    Validators.pattern(/^([A-Z](([0-9][0-9]?)|([A-Z][0-9][0-9]?)|([A-Z]?[0-9][A-Z])) ?[0-9][ABD-HJLNP-UW-Z]{2})$/i),
  ]);

  /**
   * Quick address details for selected address
   *
   * @type {EventEmitter<FullAddress>}
   * @memberof AddressLookupComponent
   */
  constructor(private api: NodeAPIService,
    private state: DipStateService,
    private service: AddressComponentService) {
    this.service.controlMode.subscribe((next) => {
      this.status = next;
    });
  }

  /**
   * Initialise component
   *
   * @memberof AddressLookupComponent
   */
  ngOnInit() {
    this.hasSearched.setValue(null);
    this.hasAddressSelected.setValue(null);
    this.formSearch = new UntypedFormGroup({
      hasSearched: this.hasSearched,
      hasAddressSelected: this.hasAddressSelected,
      postcode: this.postcode,
      matchedAddress: new UntypedFormControl(''),
    });

    this.searchResults = [];
    this.onChanges();
  }
  /**
   * Field validation for postcode
   *
   * @readonly
   * @memberof AddressLookupComponent
   */
  get isPostcodeInvalid(): boolean {
    const field = this.formSearch.get('postcode');
    return field.invalid && (field.touched || field.dirty);
  }

  /**
   * Track changes for form fields
   *
   * @memberof AddressLookupComponent
   */
  onChanges(): void {
    this.formSearch.get('matchedAddress').valueChanges.subscribe((val) => {
      this.doGetAddress();
      this.hasAddressSelected.setValue(true);
      this.hasAddressSelected.markAsTouched({ onlySelf: true });
      this.hasSearched.setValue(true);
      this.hasSearched.markAsTouched({ onlySelf: true });
      this.lookup_form_out.emit(this.formSearch);
    });
    this.formSearch.get('postcode').valueChanges
      .subscribe((val) => {
        if (!_.isNil(val)) {
          this.isSearchReturnedNoResults = false;
          this.hasSearched.setErrors({ required: true });
          this.hasSearched.markAsTouched({ onlySelf: true });
          this.lookup_form_out.emit(this.formSearch);
        }
      });
  }
  /**
   * Switch to adderss edit view
   *
   * @memberof AddressLookupComponent
   */
  switchManualEdit(): void {
    this.service.controlMode.next(1);
  }
  /**
   * Call API for perform address search
   *
   * @memberof AddressLookupComponent
   */
  doGetAddress(): void {
    this._isLoading = true;
    const id = this.formSearch.get('matchedAddress').value;
    this.api.getAddress(id)
      .subscribe(
        (success) => {
          try {
            this._isLoading = false;
            const details = (success as FullAddress); //JSON.parse(success.text());
            this.service.qasAddress = details;
            this.service.editMode = 0;
            this.service.controlMode.next(1);
          } catch (error) {
            this.formSearch.get('postcode').setErrors({ invalid: true });
          }
        },
        (error) => {
          this._isLoading = false;
          this.formSearch.get('postcode').setErrors({ invalid: true });
          if (error.status === 404) {
          } else {
          }
        },
      );
  }

  /**
   * Call API to retrieve full address for selection
   *
   * @memberof AddressLookupComponent
   */
  doLookup(): void {
    this.hasSearched.setValue(true);
    this.formSearch.get('hasAddressSelected').setErrors({ required: true });
    this.searchResults = [];
    this.isSearchReturnedNoResults = false;
    if (this.formSearch.get('postcode').valid) {
      this._isLoading = true;
      const postcode = this.formSearch.get('postcode').value;
      this.api.searchPostcode(postcode)
        .subscribe(
          (success) => {
            try {
              this._isLoading = false;
              const details = (success as Array<MatchedAddress>); //JSON.parse(success.text());
              this.searchResults = details;
              this.lookup_form_out.emit(this.formSearch);
            } catch (error) {
              this.formSearch.get('postcode').setErrors({ invalid: true });
            }
          },
          (error) => {
            this._isLoading = false;
            this.formSearch.get('postcode').setErrors({ invalid: true });
            if (error.status === 404) {
              this.isSearchReturnedNoResults = true;
            } else {
            }
          },
        );
    } else {
      this.formSearch.get('postcode').markAsTouched();
    }
  }
}
